import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  // SignalIcon, // Removed - no longer needed
  // SparklesIcon, // Unused - removed
  // PhotoIcon, // Unused - removed
  DocumentIcon,
  WrenchScrewdriverIcon,
  // ClipboardDocumentListIcon, // Unused - removed
  UserGroupIcon,
  CubeIcon,
  CogIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ChatBubbleLeftRightIcon,
  PhoneIcon,
  ListBulletIcon,
  ChartBarIcon,
  CameraIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

// Import tab components (we'll create these)
import VesselDetailsTab from './tabs/VesselDetailsTab';
import VesselCrewTab from './tabs/VesselCrewTab';
import VesselMechanicalTab from './tabs/VesselMechanicalTab';
import VesselInventoryTab from './tabs/VesselInventoryTab';
import VesselEquipmentTab from './tabs/VesselEquipmentTab';
import VesselMaintenanceTab from './tabs/VesselMaintenanceTab';
import VesselTasksTab from './tabs/VesselTasksTab';
import VesselNotesTab from './tabs/VesselNotesTab';
import VesselContactsTab from './tabs/VesselContactsTab';
import VesselChecklistsTab from './tabs/VesselChecklistsTab';
import VesselReadingsTab from './tabs/VesselReadingsTab';
import VesselPhotosTab from './tabs/VesselPhotosTab';
import VesselDocumentsTab from './tabs/VesselDocumentsTab';
import VesselSeasonsTab from './VesselSeasonsTab';


const VesselDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [vessel, setVessel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('DETAILS');
  const [showEditModal, setShowEditModal] = useState(false);

  const tabs = [
    { id: 'DETAILS', label: 'DETAILS', icon: DocumentTextIcon },
    { id: 'CREW', label: 'CREW', icon: UserGroupIcon },
    { id: 'MECHANICAL', label: 'MECHANICAL', icon: WrenchScrewdriverIcon },
    { id: 'INVENTORY', label: 'INVENTORY', icon: CubeIcon },
    { id: 'EQUIPMENT', label: 'EQUIPMENT', icon: CogIcon },
    { id: 'MAINTENANCE', label: 'MAINTENANCE', icon: CalendarDaysIcon },
    { id: 'TASKS', label: 'TASKS', icon: CheckCircleIcon },
    { id: 'NOTES', label: 'NOTES', icon: ChatBubbleLeftRightIcon },
    { id: 'CONTACTS', label: 'CONTACTS', icon: PhoneIcon },
    { id: 'CHECKLISTS', label: 'CHECKLISTS', icon: ListBulletIcon },
    { id: 'READINGS', label: 'READINGS', icon: ChartBarIcon },
    { id: 'PHOTOS', label: 'PHOTOS', icon: CameraIcon },
    { id: 'DOCUMENTS', label: 'DOCUMENTS', icon: DocumentIcon },
    { id: 'SEASONS', label: 'SEASONS', icon: CalendarDaysIcon }
  ];

  // Debug: Log tabs to console
  console.log('VesselDetail tabs:', tabs.map(t => t.label));

  useEffect(() => {
    if (isAuthenticated) {
      loadVessel();
    }
  }, [id, isAuthenticated]);

  const loadVessel = async () => {
    setLoading(true);
    try {
      console.log('🔄 VesselDetail: Loading vessel', id);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/vessels/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch vessel');
      }
      
      const data = await response.json();
      console.log('✅ VesselDetail: Loaded vessel data:', {
        id: data.vessel.id,
        name: data.vessel.vessel_name,
        image_urls_count: data.vessel.image_urls?.length || 0,
        image_urls: data.vessel.image_urls,
        primary_image: data.vessel.primary_image_url
      });
      setVessel(data.vessel);
    } catch (error) {
      console.error('❌ VesselDetail: Error loading vessel:', error);
      toast.error('Failed to load vessel');
      navigate('/vessels');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateVessel = async (updatedFields) => {
    try {
      console.log('🚢 VesselDetail: handleUpdateVessel called with:', updatedFields);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/vessels/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedFields)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update vessel');
      }

      const responseData = await response.json();
      console.log('✅ VesselDetail: Update response:', responseData);

      toast.success('Vessel updated successfully');
      
      console.log('🔄 VesselDetail: Reloading vessel data...');
      await loadVessel(); // Reload vessel data to reflect changes
      console.log('✅ VesselDetail: Vessel reloaded');
    } catch (error) {
      console.error('❌ VesselDetail: Error updating vessel:', error);
      toast.error(error.message || 'Failed to update vessel');
    }
  };

  const handleDeleteVessel = async () => {
    toast((t) => (
      <div className="flex flex-col space-y-2">
        <span className="font-medium text-red-600">Delete Vessel</span>
        <span className="text-sm text-gray-600">This action cannot be undone.</span>
        <div className="flex space-x-2">
          <button
            onClick={() => {
              toast.dismiss(t.id);
              performDeleteVessel();
            }}
            className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
          >
            Delete
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </div>
    ), { duration: 10000 });
    return;
  };

  const performDeleteVessel = async () => {

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/vessels/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete vessel');
      }

      toast.success('Vessel deleted successfully');
      navigate('/vessels');
    } catch (error) {
      console.error('Error deleting vessel:', error);
      toast.error(error.message || 'Failed to delete vessel');
    }
  };


  const getVesselTypeIcon = (type) => {
    switch (type) {
      case 'sailboat': return '⛵';
      case 'powerboat': return '🚤';
      case 'yacht': return '🛥️';
      case 'catamaran': return '⛵';
      case 'commercial': return '🚢';
      default: return '🚤';
    }
  };

  const renderTabContent = () => {
    if (!vessel) return null;

    const tabProps = {
      vessel,
      onVesselUpdate: handleUpdateVessel,
      user
    };

    switch (activeTab) {
      case 'DETAILS':
        return <VesselDetailsTab {...tabProps} />;
      case 'CREW':
        return <VesselCrewTab {...tabProps} />;
      case 'MECHANICAL':
        return <VesselMechanicalTab {...tabProps} />;
      case 'INVENTORY':
        return <VesselInventoryTab {...tabProps} />;
      case 'EQUIPMENT':
        return <VesselEquipmentTab {...tabProps} />;
      case 'MAINTENANCE':
        return <VesselMaintenanceTab {...tabProps} />;
      case 'TASKS':
        return <VesselTasksTab {...tabProps} />;
      case 'NOTES':
        return <VesselNotesTab {...tabProps} />;
      case 'CONTACTS':
        return <VesselContactsTab {...tabProps} />;
      case 'CHECKLISTS':
        return <VesselChecklistsTab {...tabProps} />;
      case 'READINGS':
        return <VesselReadingsTab {...tabProps} />;
      case 'PHOTOS':
        return <VesselPhotosTab {...tabProps} />;
      case 'DOCUMENTS':
        return <VesselDocumentsTab {...tabProps} />;
      case 'SEASONS':
        return <VesselSeasonsTab vesselId={vessel?.id} />;
      default:
        return <VesselDetailsTab {...tabProps} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading vessel details...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Please log in to view vessel details
            </h2>
          </div>
        </div>
      </div>
    );
  }

  if (!vessel) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Vessel not found</h2>
          <button
            onClick={() => navigate('/vessels')}
            className="btn btn-primary"
          >
            Back to Vessels
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/vessels')}
                className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
              <div className="flex items-center space-x-3">
                <div className="text-3xl">{getVesselTypeIcon(vessel.vessel_type)}</div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">{vessel.vessel_name}</h1>
                  <p className="text-sm text-gray-500">{vessel.manufacturer} {vessel.model} ({vessel.year_built})</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex space-x-2">
                <button
                  onClick={() => setShowEditModal(true)}
                  className="btn btn-secondary flex items-center"
                >
                  <PencilIcon className="h-4 w-4 mr-2" />
                  Edit
                </button>
                <button
                  onClick={handleDeleteVessel}
                  className="btn btn-danger flex items-center"
                >
                  <TrashIcon className="h-4 w-4 mr-2" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default VesselDetail;
